import groupBy from "lodash.groupby";

import { ensureFullUrl } from "@intelligems/ig-plugin/core/redirectUtils";
import { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import { ShopifyTemplateTypeType } from "@intelligems/ig-types/dtos/shopify";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { getSpecificRedirectsUtil } from "@src/features/experiments/components/redirects/getSpecificRedirects";

import { initialRedirectState } from "../../initials";

/**
 * A template test group's `templateSuffixes` is the set of templates the
 * storefront enrolls visitors from, so it must be exactly the templates the
 * group's variations currently point at (their `view` query params). It is
 * derived from those selections rather than accumulated onto, so a template
 * that was swapped out, or one inherited from a duplicated experience, drops
 * out instead of continuing to enroll visitors.
 *
 * Experiments only: personalizations keep a source template in
 * `templateSuffixes` that has no `view` counterpart, see
 * `syncPersonalizationTemplateSuffixes`.
 */
export const syncTemplateSuffixes = (
  _draft: RedirectDto[],
  group: number
): RedirectDto[] => {
  const isInGroup = (redirect: RedirectDto) =>
    redirect.group === group && redirect.isTemplateTest;

  const groupSuffixes = [
    ...new Set(
      _draft
        .filter(isInGroup)
        .flatMap((redirect) => redirect.queryParams)
        .filter((param) => param.key === "view" && param.value)
        .map((param) => param.value)
    ),
  ];

  // Redirects already carrying the right set are returned as-is, so callers
  // can resync unconditionally without producing a state change.
  const isInSync = (redirect: RedirectDto) =>
    redirect.templateSuffixes?.length === groupSuffixes.length &&
    groupSuffixes.every((suffix) =>
      redirect.templateSuffixes?.includes(suffix)
    );

  return _draft.map((redirect) =>
    isInGroup(redirect) && !isInSync(redirect)
      ? { ...redirect, templateSuffixes: [...groupSuffixes] }
      : redirect
  );
};

/**
 * A personalization template redirect enrolls visitors on its source template
 * ("Anyone viewing a page that uses this template", kept at index 0) and on
 * the target it sends them to (its `view` query param), so its
 * `templateSuffixes` is exactly that pair. It is derived per redirect rather
 * than accumulated onto, so re-selecting the target drops the previous one.
 */
export const syncPersonalizationTemplateSuffixes = (
  redirect: RedirectDto
): RedirectDto => {
  const source = redirect.templateSuffixes?.[0];
  const targets = redirect.queryParams
    .filter((param) => param.key === "view" && param.value)
    .map((param) => param.value);

  return {
    ...redirect,
    templateSuffixes: [
      ...new Set(
        [source, ...targets].filter((suffix): suffix is string => !!suffix)
      ),
    ],
  };
};

export const syncAllTemplateSuffixes = (_draft: RedirectDto[]) =>
  [
    ...new Set(
      _draft
        .filter((redirect) => redirect.isTemplateTest)
        .map((redirect) => redirect.group)
    ),
  ].reduce(
    (redirects, group) => syncTemplateSuffixes(redirects, group),
    _draft
  );

export const ensureAllTemplateType = (
  _draft: RedirectDto[],
  controlTemplateType: ShopifyTemplateTypeType,
  group: number
): RedirectDto[] => {
  return _draft.map((redirect) => {
    if (
      redirect.group === group &&
      controlTemplateType !== redirect.templateType &&
      redirect.isTemplateTest
    ) {
      return {
        ...redirect,
        templateType:
          redirect.group === group
            ? controlTemplateType
            : redirect.templateType,
        queryParams: [],
      };
    } else {
      return redirect;
    }
  });
};

export const addGroupRedirect = <T extends RedirectDto[]>(
  draft: RedirectDto[],
  testGroup: VariationFe,
  group: number,
  isThemeTest: boolean,
  isTemplateTest: boolean
): RedirectDto[] => {
  const index = draft.findIndex((redirect) => redirect.group === group);
  if (isThemeTest) {
    draft.push(
      initialRedirectState({ variation: testGroup, group, isThemeTest: true })
    );
  } else if (index !== -1) {
    if (isTemplateTest) {
      draft.push(
        initialRedirectState({
          variation: testGroup,
          group: group,
          isThemeTest: false,
          templateType: draft[index].templateType!,
          isTemplateTest: true,
        })
      );
    } else {
      draft.push(
        initialRedirectState({
          variation: testGroup,
          group,
          isThemeTest: false,
          templateType: null,
          isTemplateTest: false,
          copyRedirect: draft[index],
        })
      );
    }
  }

  return draft;
};

export const deleteRedirectsByIds = (
  draft: RedirectDto[],
  redirectIds: string[]
) => {
  draft = draft.filter((redirect) => !redirectIds.includes(redirect.id));
  return draft;
};

export const orderByTestGroup = (
  variations: VariationFe[],
  groupedRedirects: any[]
) => {
  return groupedRedirects.map((redirectGroup) => {
    return [
      redirectGroup[0],
      redirectGroup[1].sort((a, b) => {
        const variationA = variations.find((tg) => tg.id === a.variationId);
        const variationB = variations.find((tg) => tg.id === b.variationId);
        if (variationA && variationB) {
          if (variationA.order > variationB.order) {
            return 1;
          } else if (variationA.order < variationB.order) {
            return -1;
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      }),
    ];
  });
};

export const ensureTestGroupRedirects = (
  draft: RedirectDto[],
  testGroups: VariationFe[]
) => {
  const removedTgRedirects = draft.filter(
    (_) => !testGroups.map((group) => group.id).includes(_.variationId)
  );
  // remove redirects from deleted test groups
  if (removedTgRedirects.length > 0) {
    draft = deleteRedirectsByIds(
      draft,
      removedTgRedirects.map((redirect) => redirect.id)
    );
  }

  testGroups.forEach((testGroup) => {
    // URL redirects
    if (
      draft.filter(
        (redirect) => !redirect.isThemeTest && !redirect.isTemplateTest
      ).length > 0 &&
      !draft
        .filter((redirect) => !redirect.isThemeTest && !redirect.isTemplateTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      const redirectGroupKeys = Object.keys(
        groupBy(
          draft.filter((_) => !_.isThemeTest && !_.isTemplateTest),
          (_) => _.group
        )
      );

      redirectGroupKeys.forEach((group) => {
        // update redirect "group" w new test group field
        draft = addGroupRedirect(draft, testGroup, Number(group), false, false);
      });
    }

    // theme redirects
    if (
      draft.filter((redirect) => redirect.isThemeTest).length > 0 &&
      !draft
        .filter((redirect) => redirect.isThemeTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      const themeRedirectGroupKeys = Object.keys(
        groupBy(
          draft.filter((_) => _.isThemeTest),
          (_) => _.group
        )
      );
      if (themeRedirectGroupKeys) {
        draft = addGroupRedirect(
          draft,
          testGroup,
          Number(themeRedirectGroupKeys[0]),
          true,
          false
        );
      }
    }

    // template redirects
    if (
      draft.filter((redirect) => redirect.isTemplateTest).length > 0 &&
      !draft
        .filter((redirect) => redirect.isTemplateTest)
        .map((redirect) => redirect.variationId)
        .includes(testGroup.id)
    ) {
      draft = addGroupRedirect(draft, testGroup, 1, false, true);
    }
  });
  return draft;
};

/**
 * Closes gaps in url redirect group numbering so the groups read 1, 2, 3…
 *
 * `variationId` narrows this to a single group's own redirects, for the
 * per-variation mods panels. Without it the whole test is renumbered together,
 * which is what a split URL test needs: there a group is one origin page shared
 * by every variation, and all of its rows must keep the same number.
 */
export const ensureRedirectGroupOrder = (
  redirects: RedirectDto[],
  variationId?: string
) => {
  const nonUrlRedirects = redirects.filter(
    (redirect) => redirect.isTemplateTest || redirect.isThemeTest
  );

  const urlRedirects = getSpecificRedirectsUtil(redirects, "url");
  const redirectsToOrder = variationId
    ? urlRedirects.filter((redirect) => redirect.variationId === variationId)
    : urlRedirects;
  const untouchedRedirects = variationId
    ? urlRedirects.filter((redirect) => redirect.variationId !== variationId)
    : [];

  const urlRedirectsByGroup = groupBy(
    redirectsToOrder,
    (redirect) => redirect.group
  );

  const updatedRedirectsByGroup: RedirectDto[] = [];

  Object.entries(urlRedirectsByGroup)
    .sort(([groupA], [groupB]) => Number(groupA) - Number(groupB))
    .forEach(([, redirectGroup], index) => {
      redirectGroup.forEach((redirect) => {
        updatedRedirectsByGroup.push({ ...redirect, group: index + 1 });
      });
    });

  return updatedRedirectsByGroup
    .concat(untouchedRedirects)
    .concat(nonUrlRedirects);
};

export const ensureUrlRedirectPathFormat = (
  redirects: RedirectDto[],
  domain: string
) => {
  const updatedRedirects: RedirectDto[] = [];

  redirects.forEach((redirect) => {
    let tempRedirect: RedirectDto = redirect;
    if (!redirect.isTemplateTest && !redirect.isThemeTest) {
      if (redirect.filter === "matchesExactly" || redirect.filter === "none") {
        tempRedirect = {
          ...tempRedirect,
          originUrl: ensureFullUrl(tempRedirect, domain, "originUrl"),
        };
      }

      if (
        !redirect.skip &&
        redirect.destinationUrl &&
        redirect.destinationUrl.length > 0 &&
        redirect.redirectType === "fullReplacement"
      ) {
        tempRedirect = {
          ...tempRedirect,
          destinationUrl: ensureFullUrl(tempRedirect, domain),
        };
      }
    }
    updatedRedirects.push(tempRedirect);
  });

  return updatedRedirects;
};
